import { z } from 'zod';
import { E164PhoneSchema, TimestampSchema, UuidSchema } from './common';

export const LeadStatusSchema = z.enum(['new', 'engaged', 'opted_out', 'converted', 'invalid']);
export type LeadStatus = z.infer<typeof LeadStatusSchema>;

export const LeadSourceSchema = z.enum(['csv-import', 'meta-ads', 'manual', 'api']);
export type LeadSource = z.infer<typeof LeadSourceSchema>;

export const LeadCustomFieldsSchema = z.record(z.union([z.string(), z.number(), z.boolean()]));
export type LeadCustomFields = z.infer<typeof LeadCustomFieldsSchema>;

export const LeadSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  name: z.string().min(1).optional(),
  phone: E164PhoneSchema,
  email: z.string().email().optional(),
  status: LeadStatusSchema,
  source: LeadSourceSchema,
  tags: z.array(z.string()),
  customFields: LeadCustomFieldsSchema.optional(),
  lastContactAt: TimestampSchema.optional(),
  convertedAt: TimestampSchema.optional(),
  notes: z.string().optional(),
  totalRevenueCents: z.number().int().nonnegative(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Lead = z.infer<typeof LeadSchema>;

export const LeadDddPointSchema = z.object({
  ddd: z.string().regex(/^\d{2}$/),
  count: z.number().int().nonnegative(),
});
export type LeadDddPoint = z.infer<typeof LeadDddPointSchema>;

/**
 * Agregação por UF (estado brasileiro). DDDs são mapeados pra UF via tabela
 * Anatel — SP cobre 9 DDDs, MG cobre 7, etc. Útil pra "heatmap geográfico"
 * em granularidade estadual (mais legível que DDD pra dashboards executivos).
 *
 * `revenueCents` é a soma da receita atribuída aos leads daquele estado —
 * permite ranking por dinheiro (não só count).
 */
export const LeadStatePointSchema = z.object({
  uf: z.string().length(2),
  count: z.number().int().nonnegative(),
  revenueCents: z.number().int().nonnegative(),
});
export type LeadStatePoint = z.infer<typeof LeadStatePointSchema>;

export const LeadsEvolutionPointSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  count: z.number().int().nonnegative(),
});
export type LeadsEvolutionPoint = z.infer<typeof LeadsEvolutionPointSchema>;

export const LeadStatusBreakdownSchema = z.object({
  new: z.number().int().nonnegative(),
  engaged: z.number().int().nonnegative(),
  opted_out: z.number().int().nonnegative(),
  converted: z.number().int().nonnegative(),
  invalid: z.number().int().nonnegative(),
});
export type LeadStatusBreakdown = z.infer<typeof LeadStatusBreakdownSchema>;

export const LeadSourceBreakdownSchema = z.object({
  'csv-import': z.number().int().nonnegative(),
  'meta-ads': z.number().int().nonnegative(),
  manual: z.number().int().nonnegative(),
  api: z.number().int().nonnegative(),
});
export type LeadSourceBreakdown = z.infer<typeof LeadSourceBreakdownSchema>;

/**
 * Stats agregados pra topo da tela /leads. Espelha aproximadamente o que
 * Revenue retorna em `/api/leads stats` (averageTicket, totalRevenue,
 * salesCount, dddHeatmap) + breakdown de status e source pros donut charts
 * + evolution dos últimos 30 dias pra line chart.
 *
 * `totalRevenueCents`/`averageTicketCents` em centavos pra evitar float drift.
 * `dddHeatmap` limitado a top 30 DDDs no backend (Brasil tem 67 DDDs ativos
 * mas top 10-20 cobrem 90%+ do volume real).
 */
/**
 * Subject Access Request (LGPD Art. 18). Devolve em JSON estruturado tudo o
 * que o sistema sabe sobre o lead — campos diretos + mensagens enviadas +
 * conversations + runs de cadência. Pensado pra entregar como anexo de
 * resposta ao titular dos dados.
 */
export const LeadExportMessageSchema = z.object({
  id: UuidSchema,
  status: z.string(),
  recipientPhone: z.string(),
  recipientName: z.string().nullable(),
  templateId: UuidSchema.nullable(),
  cadenceRunId: UuidSchema.nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const LeadExportConversationSchema = z.object({
  id: UuidSchema,
  status: z.string(),
  contactPhone: z.string(),
  contactName: z.string().nullable(),
  lastMessagePreview: z.string(),
  lastMessageAt: TimestampSchema,
  unreadCount: z.number().int().nonnegative(),
  assignedTo: z.string().nullable(),
});

export const LeadExportCadenceRunSchema = z.object({
  id: UuidSchema,
  cadenceId: UuidSchema,
  status: z.string(),
  currentLayerId: UuidSchema.nullable(),
  startedAt: TimestampSchema,
  lastFiredAt: TimestampSchema.nullable(),
  completedAt: TimestampSchema.nullable(),
});

export const LeadExportResponseSchema = z.object({
  lead: LeadSchema,
  messages: z.array(LeadExportMessageSchema),
  conversations: z.array(LeadExportConversationSchema),
  cadenceRuns: z.array(LeadExportCadenceRunSchema),
  exportedAt: TimestampSchema,
});
export type LeadExportResponse = z.infer<typeof LeadExportResponseSchema>;

export const DeleteLeadInputSchema = z.object({
  reason: z.string().min(1).max(500).optional(),
});
export type DeleteLeadInput = z.infer<typeof DeleteLeadInputSchema>;

export const LeadsStatsResponseSchema = z.object({
  totalLeads: z.number().int().nonnegative(),
  totalRevenueCents: z.number().int().nonnegative(),
  salesCount: z.number().int().nonnegative(),
  averageTicketCents: z.number().int().nonnegative(),
  byStatus: LeadStatusBreakdownSchema,
  bySource: LeadSourceBreakdownSchema,
  dddHeatmap: z.array(LeadDddPointSchema),
  stateHeatmap: z.array(LeadStatePointSchema),
  evolution: z.array(LeadsEvolutionPointSchema),
});
export type LeadsStatsResponse = z.infer<typeof LeadsStatsResponseSchema>;

/**
 * Contagem real de leads que casam com um conjunto de filtros de segmento —
 * usada pelo wizard de campanha pra mostrar estimativa de audiência exata
 * (substitui a "amostra de 100" filtrada client-side, enganosa em tenants
 * grandes). Os filtros espelham `CampaignAudienceLeadSegmentFilters`.
 */
export const SegmentCountInputSchema = z.object({
  tags: z.array(z.string().min(1)).optional(),
  status: z.array(LeadStatusSchema).optional(),
  source: z.array(LeadSourceSchema).optional(),
});
export type SegmentCountInput = z.infer<typeof SegmentCountInputSchema>;

export const SegmentCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type SegmentCountResponse = z.infer<typeof SegmentCountResponseSchema>;

export const LeadsListQuerySchema = z.object({
  status: LeadStatusSchema.optional(),
  source: LeadSourceSchema.optional(),
  tag: z.string().min(1).optional(),
  q: z.string().min(1).optional(),
  /** Filtra leads com `createdAt >= dateFrom` (ISO 8601). "Desde quando" do
   * histórico — usado pelos presets de período da tela /leads. */
  dateFrom: z.string().datetime().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type LeadsListQuery = z.infer<typeof LeadsListQuerySchema>;

export const LeadsListResponseSchema = z.object({
  leads: z.array(LeadSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type LeadsListResponse = z.infer<typeof LeadsListResponseSchema>;

export const CreateLeadInputSchema = z.object({
  name: z.string().optional(),
  phone: E164PhoneSchema,
  email: z.string().email().or(z.literal('')).optional(),
  status: LeadStatusSchema.default('new'),
  source: LeadSourceSchema.default('manual'),
  tags: z.array(z.string()).default([]),
  customFields: LeadCustomFieldsSchema.optional(),
  notes: z.string().optional(),
});
export type CreateLeadInput = z.infer<typeof CreateLeadInputSchema>;

export const UpdateLeadInputSchema = z.object({
  name: z.string().optional(),
  phone: E164PhoneSchema.optional(),
  email: z.string().email().or(z.literal('')).optional(),
  status: LeadStatusSchema.optional(),
  source: LeadSourceSchema.optional(),
  tags: z.array(z.string()).optional(),
  customFields: LeadCustomFieldsSchema.optional(),
  notes: z.string().optional(),
});
export type UpdateLeadInput = z.infer<typeof UpdateLeadInputSchema>;

export const OptOutLeadInputSchema = z.object({
  reason: z.string().min(1).optional(),
});
export type OptOutLeadInput = z.infer<typeof OptOutLeadInputSchema>;

export const BulkImportLeadInputSchema = z.object({
  name: z.string().min(1).optional(),
  phone: E164PhoneSchema,
  email: z.string().email().optional(),
  tags: z.array(z.string()).optional(),
});
export type BulkImportLeadItem = z.infer<typeof BulkImportLeadInputSchema>;

export const BulkImportLeadsInputSchema = z.object({
  // Cap em 10k pra evitar payload gigante num único POST. CSVs grandes devem
  // ir via multipart upload (planejado fase 3) que processa async em chunks.
  leads: z.array(BulkImportLeadInputSchema).min(1).max(10_000),
  defaultTags: z.array(z.string()).optional(),
});
export type BulkImportLeadsInput = z.infer<typeof BulkImportLeadsInputSchema>;

// Espelha o retorno real da rota: contagens. (Campo `leads` removido — a rota
// nunca o preencheu; o cliente parseava o schema cheio e dava ZodError.)
export const BulkImportLeadsResponseSchema = z.object({
  imported: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type BulkImportLeadsResponse = z.infer<typeof BulkImportLeadsResponseSchema>;
